import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { newId, VAULT_KINDS, type VaultKind } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import {
  blankVaultDraft, buildVaultPublish, downloadName, draftOfEntry, fileLinkOf, sizeText, titleOf, typeText, useVaultDocuments, useVaultLinks, useVaultVersions, vaultEntries, vaultProblems,
  type VaultDraft, type VaultEntry, type VaultLink, type VaultVersionRow,
} from '../data/documents.ts'
import { fmtDay } from '../data/planSetup.ts'
import { DocumentTooLargeError, DocumentTypeError, prepareDocument, type PreparedDocument } from '../photos/capture.ts'
import { readDocument } from '../photos/lane.ts'
import { documentPublishOptions, forgetRefusedBytes, PhotoQuotaError } from '../photos/store.ts'
import { useBlobs } from '../photos/usePhotos.ts'
import type { Pending } from '../sync/merge.ts'
import { BackLink, Banner, DisabledHint, Field, NotAllowed, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { Picto } from '../ui/pictos.tsx'
import { TwoTap, useCloseOnPending } from './workshop/shared.tsx'

type Action = ReturnType<typeof useWorkshopAction>

/** Réglages › Documents (docs/M15.md V, the document vault): the club's labels, safety-data sheets, notices and policies, by kind, each with its version in
 *  force, its file (type and size; "Télécharger" hands it to the phone's own viewer — never previewed here) and its history; the direction publishes a new
 *  document or the next version of one with its PDF (or a photo of the label) and retires a version (two taps). Procedures stay in Planning › Réglages.
 *  The lists read the mirror (offline); publishing, retiring and downloading need the network (`document.publish_version@2` / `document.retire_version@1`:
 *  intents, the server's verdict; the bytes leave with the publish in one local transaction and the lane uploads them). Who reads the club's documents
 *  (procedure.read) sees the page; the direction (procedure.publish) changes it. */
export function DocumentsPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { can } = useClub()
  const readable = can('procedure.read')
  const access = useWriteAccess('procedure.publish')
  const online = useEngineState().online !== false
  const docs = useVaultDocuments(); const versions = useVaultVersions(); const links = useVaultLinks()
  const w = useWorkshopAction()
  const [editing, setEditing] = useState<{ entry: VaultEntry | null } | null>(null)
  const lists = useMemo(() => vaultEntries(docs ?? [], versions ?? [], links ?? [], lang), [docs, versions, links, lang])
  const head = <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="clipboard" className="size-7 text-brand dark:text-fairway" />{t('documents.title')}</h1>
  if (!readable) return <section className="flex flex-col gap-4" data-testid="documents-page">{head}<NotAllowed permission="procedure.read" /></section>
  if (!docs || !versions || !links) return <p>{t('common.loading')}</p>
  if (editing) return <VaultForm key={editing.entry?.doc.id ?? 'new'} entry={editing.entry} w={w} canSave={access.allowed && online} online={online} onClose={() => setEditing(null)} />
  return (
    <section className="flex flex-col gap-4" data-testid="documents-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      {head}
      <p className="meta">{t('documents.help')}</p>
      {w.pending && <PendingBanner testId="documents-pending" />}
      {w.error && <Banner tone="error"><span data-testid="documents-error">{w.error}</span></Banner>}
      {w.ok && <Banner tone="ok"><span data-testid="documents-ok">{w.ok}</span></Banner>}
      {access.permitted && (
        <div className="flex flex-col gap-1">
          <button type="button" className="btn btn-primary" disabled={!access.allowed || !online} onClick={() => { w.reset(); setEditing({ entry: null }) }} data-testid="documents-new">{t('documents.new')}</button>
          <DisabledHint show={!online} testId="documents-offline">{t('documents.needsNetwork')}</DisabledHint>
        </div>
      )}
      {VAULT_KINDS.map((kind) => (
        <KindSection key={kind} kind={kind} entries={lists[kind]} links={links} w={w} canChange={access.allowed && online} onNewVersion={(e) => { w.reset(); setEditing({ entry: e }) }} />
      ))}
    </section>
  )
}

function KindSection({ kind, entries, links, w, canChange, onNewVersion }: { kind: VaultKind; entries: VaultEntry[]; links: readonly VaultLink[]; w: Action; canChange: boolean; onNewVersion: (e: VaultEntry) => void }) {
  const { t } = useTranslation()
  const { can } = useClub()
  return (
    <section className="flex flex-col gap-3" data-testid={`documents-kind-${kind}`} data-count={entries.length}>
      <h2 className="flex items-center justify-between gap-2 text-xl font-bold">
        <span className="min-w-0">{t(`documents.kind.${kind}`)}</span>
        <span className="meta shrink-0 whitespace-nowrap font-normal">{t('documents.count', { count: entries.length })}</span>
      </h2>
      {entries.length === 0 && <p className="meta" data-testid={`documents-none-${kind}`}>{t('documents.none')}</p>}
      <ul className="flex flex-col gap-3">
        {entries.map((e) => <DocCard key={e.doc.id} entry={e} links={links} w={w} canChange={canChange && can('procedure.publish')} onNewVersion={() => onNewVersion(e)} />)}
      </ul>
    </section>
  )
}

function DocCard({ entry, links, w, canChange, onNewVersion }: { entry: VaultEntry; links: readonly VaultLink[]; w: Action; canChange: boolean; onNewVersion: () => void }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const shown = entry.current ?? entry.latest
  const title = titleOf(shown.title, lang)
  const retired = !entry.current
  const note = titleOf(shown.summary, lang)
  return (
    <li className={`card flex flex-col gap-2 ${retired ? 'opacity-90' : ''}`} data-testid="document-row" data-document-id={entry.doc.id} data-version-id={shown.id} data-status={retired ? 'retired' : 'published'}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 break-words text-lg font-bold" data-testid="document-title">{title}</p>
        <span className="flex flex-wrap gap-1.5">
          {retired ? <Pill tone="gray">{t('documents.retiredPill')}</Pill> : <Pill tone="green">{t('documents.version', { n: shown.version_no })}</Pill>}
          {shown._pending && <Pill tone="amber">{t('common.pendingSend')}</Pill>}
        </span>
      </div>
      <p className="meta">{retired && shown.retired_at ? t('documents.retiredOn', { date: fmtDay(shown.retired_at.slice(0, 10)) }) : t('documents.effective', { date: fmtDay(shown.effective_from) })}</p>
      {note && <p className="text-sm" data-testid="document-note">{note}</p>}
      <FileLine file={entry.file} title={title} versionNo={shown.version_no} />
      {canChange && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="button" className="btn" disabled={w.busy} onClick={onNewVersion} data-testid="document-new-version">{t('documents.newVersion')}</button>
          {entry.current && (
            <TwoTap label={t('documents.retire')} confirm={t('documents.retireConfirm')} danger disabled={w.busy} testId="document-retire"
              onConfirm={() => void w.run('document.retire_version@1', { document_version_id: entry.current!.id }, 'documents.retired', { aggregateId: entry.doc.id })} />
          )}
        </div>
      )}
      {entry.history.length > 1 && <History versions={entry.history} links={links} />}
    </li>
  )
}

/** Every version, newest first, with its file: an older label is what an older verification named. */
function History({ versions, links }: { versions: Array<Pending<VaultVersionRow>>; links: readonly VaultLink[] }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  return (
    <details className="group" data-testid="document-history">
      <summary className="flex min-h-14 cursor-pointer items-center text-base font-semibold" data-testid="document-history-toggle">{t('documents.history', { count: versions.length })}</summary>
      <ul className="mt-2 flex flex-col gap-2">
        {versions.map((v) => {
          const file = fileLinkOf(links, v.id)
          return (
            <li key={v.id} className="flex flex-col gap-1 rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="document-version-row" data-version-no={v.version_no} data-status={v.status}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{t('documents.version', { n: v.version_no })} · {titleOf(v.title, lang)}</p>
                <Pill tone={v.status === 'published' ? 'green' : 'gray'}>{v.status === 'published' ? t('documents.inForce') : t('documents.retiredPill')}</Pill>
              </div>
              <p className="meta">{t('documents.effective', { date: fmtDay(v.effective_from) })}{v.retired_at ? ` · ${t('documents.retiredOn', { date: fmtDay(v.retired_at.slice(0, 10)) })}` : ''}</p>
              <FileLine file={file} title={titleOf(v.title, lang)} versionNo={v.version_no} testId="document-version-file" />
            </li>
          )
        })}
      </ul>
    </details>
  )
}

/** "PDF · 2,4 Mo · etiquette.pdf" and the way to hand it to the phone: the device's own copy while the lane still holds it, else storage (online). */
export function FileLine({ file, title, versionNo, testId = 'document-file' }: { file: VaultLink | undefined; title: string; versionNo: number; testId?: string }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { transport, org } = useClub()
  const blobs = useBlobs()
  const online = useEngineState().online !== false
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (!file) return <p className="meta" data-testid={`${testId}-none`}>{t('documents.noFile')}</p>
  const local = blobs.get(file.sha256)
  const download = async () => {
    setBusy(true); setError(null)
    try {
      const blob = await readDocument({ transport }, file, org.org_id, local?.blob)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = downloadName(file, title, versionNo); a.rel = 'noopener'
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch { setError(t('documents.downloadFailed')) } finally { setBusy(false) }
  }
  return (
    <div className="flex flex-col gap-2" data-testid={testId} data-mime={file.mime} data-bytes={file.bytes} data-bucket={file.bucket ?? 'attachments'}>
      <p className="flex min-w-0 flex-wrap items-center gap-x-2 text-sm">
        <span className="font-semibold">{typeText(file.mime)}</span><span className="meta">· {sizeText(file.bytes, lang)}</span>
        {file.caption && <span className="min-w-0 truncate" data-testid={`${testId}-name`}>· {file.caption}</span>}
        {local && local.state !== 'failed' && <Pill tone="amber">{t('documents.waiting')}</Pill>}
      </p>
      <button type="button" className="btn" disabled={busy || (!local && !online)} onClick={() => void download()} data-testid={`${testId}-download`}>
        {busy ? t('documents.downloading') : t('documents.download')}
      </button>
      <DisabledHint show={!local && !online} testId={`${testId}-offline`}>{t('documents.downloadNeedsNetwork')}</DisabledHint>
      {error && <Banner tone="warn"><span data-testid={`${testId}-error`}>{error}</span></Banner>}
    </div>
  )
}

/** A new document (its kind, its titles, its file) or the next version of one (the kind kept; the titles and the note start from the version shown; the
 *  file is chosen again: a version IS its file). The publish is answered at once (sendAndWait); the bytes leave the page with it in one local transaction
 *  and the lane uploads them; refused, the form stays with the reason and the bytes are let go. */
function VaultForm({ entry, w, canSave, online, onClose }: { entry: VaultEntry | null; w: Action; canSave: boolean; online: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { db, org, site } = useClub()
  const ids = useRef({ documentId: entry?.doc.id ?? newId(), versionId: newId() })
  const [d, setD] = useState<VaultDraft>(() => (entry ? draftOfEntry(entry) : blankVaultDraft()))
  const [prepared, setPrepared] = useState<PreparedDocument | null>(null)
  const [picking, setPicking] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const problems = vaultProblems(d)
  // kept on the device (no network at the tap): the form closes and the list says so (M23 item 10); the bytes wait with the publish
  useCloseOnPending(w.pending, onClose)
  const pick = async (f: File) => {
    setPicking(true); setFileError(null)
    try {
      const p = await prepareDocument(f)
      setPrepared(p)
      setD((x) => ({ ...x, file: { sha256: p.sha256, bytes: p.bytes, mime: p.mime, capturedAt: p.capturedAt, name: p.name } }))
    } catch (e) {
      setFileError(e instanceof DocumentTooLargeError ? t('documents.err.ATTACHMENT_TOO_LARGE') : e instanceof DocumentTypeError ? t('documents.err.ATTACHMENT_TYPE_NOT_ALLOWED') : t('errors.unknown'))
    } finally { setPicking(false) }
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0 || !prepared || !canSave) return
    let opts
    try { opts = await documentPublishOptions(db, org.org_id, site.site_id, prepared) } catch (err) {
      setFileError(err instanceof PhotoQuotaError ? t('photos.quota') : err instanceof Error && err.message ? err.message : t('errors.unknown'))
      return
    }
    const ok = await w.run('document.publish_version@2', buildVaultPublish(ids.current, d, entry === null), 'documents.published', { ...opts, aggregateId: ids.current.documentId })
    if (ok) { onClose(); return }
    // refused: the bytes it brought go (kept while the publish is still on its way: the lane sends them once it is applied)
    await forgetRefusedBytes(db, prepared.sha256)
  }
  const shown = entry ? entry.current ?? entry.latest : null
  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" data-testid="document-form">
      <button type="button" className="-mx-2 inline-flex min-h-14 w-full items-center gap-2 px-2 text-left text-base font-semibold" onClick={onClose} data-testid="document-cancel">← {t('common.back')}</button>
      <h1 className="text-2xl font-bold">{shown ? t('documents.versionTitle', { title: titleOf(shown.title, lang) }) : t('documents.newTitle')}</h1>
      {shown && <Banner tone="info">{t('documents.versionHelp', { n: shown.version_no, next: (entry?.latest.version_no ?? 0) + 1 })}</Banner>}
      {!entry && (
        <Field label={t('documents.form.kind')} htmlFor="doc-kind">
          <select id="doc-kind" className="input" value={d.kind} onChange={(e) => setD({ ...d, kind: e.target.value as VaultKind })} data-testid="document-kind">
            {VAULT_KINDS.map((k) => <option key={k} value={k}>{t(`documents.kindOne.${k}`)}</option>)}
          </select>
        </Field>
      )}
      <Field label={t('documents.form.titleFr')} hint={t('documents.form.titleHint')} htmlFor="doc-title-fr">
        <input id="doc-title-fr" className="input" maxLength={120} value={d.titleFr} onChange={(e) => setD({ ...d, titleFr: e.target.value })} data-testid="document-title-fr" />
      </Field>
      <Field label={t('documents.form.titleEn')} hint={t('common.optional')} htmlFor="doc-title-en">
        <input id="doc-title-en" className="input" maxLength={120} value={d.titleEn} onChange={(e) => setD({ ...d, titleEn: e.target.value })} data-testid="document-title-en" />
      </Field>
      <Field label={t('documents.form.note')} hint={t('documents.form.noteHint')} htmlFor="doc-note">
        <textarea id="doc-note" className="input min-h-24" maxLength={4000} value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} data-testid="document-note-input" />
      </Field>
      <fieldset className="flex flex-col gap-2" data-testid="document-file-field" data-chosen={d.file ? 'yes' : 'no'}>
        <legend className="text-sm font-semibold">{t('documents.form.file')}</legend>
        <p className="meta">{t('documents.form.fileHint')}</p>
        {d.file && (
          <p className="flex min-w-0 flex-wrap items-center gap-x-2 rounded-xl border-2 border-slate-300 p-3 text-sm dark:border-slate-600" data-testid="document-file-chosen" data-mime={d.file.mime}>
            <span className="font-semibold">{typeText(d.file.mime)}</span><span className="meta">· {sizeText(d.file.bytes, lang)}</span><span className="min-w-0 break-words">· {d.file.name}</span>
          </p>
        )}
        <button type="button" className="btn" disabled={picking || w.busy} onClick={() => input.current?.click()} data-testid="document-file-pick">
          {picking ? t('documents.form.preparing') : d.file ? t('documents.form.fileChange') : t('documents.form.filePick')}
        </button>
        <input ref={input} type="file" accept="application/pdf,image/*" className="sr-only" tabIndex={-1} aria-hidden data-testid="document-file-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void pick(f) }} />
        {fileError && <Banner tone="warn"><span data-testid="document-file-error">{fileError}</span></Banner>}
      </fieldset>
      {problems.length > 0 && (
        <ul className="flex flex-col gap-1" data-testid="document-problems">
          {problems.map((p) => <li key={p} className="meta" data-code={p}>{t(`documents.err.${p}`)}</li>)}
        </ul>
      )}
      {w.pending && <PendingBanner testId="document-pending" />}
      {w.error && <Banner tone="error"><span data-testid="document-error">{w.error}</span></Banner>}
      <button className="btn btn-primary" disabled={w.busy || picking || !canSave || problems.length > 0} data-testid="document-publish">{w.busy ? t('common.saving') : t('documents.publish')}</button>
      <DisabledHint show={!online} testId="document-offline">{t('documents.needsNetwork')}</DisabledHint>
    </form>
  )
}
